import $ from "jquery";

const COOKIE_EXPIRY_DAYS = 30;
const CONSENT_VERSION = "2026-04-29-v1";
const COOKIE_POLICY_VERSION = "2026-04-29-v1";

const CONSENT_COOKIES = {
    essential: [
        "user_cookie_consent",
        "user_essential_cookie_consent",
    ],
    optional: [
        "user_performance_cookie_consent",
        "user_functional_cookie_consent",
        "user_social_media_cookie_consent",
    ],
};

const CONSENT_EVENTS = {
    performance: {
        granted: "on_user_performance_cookie_consent_granted",
        denied: "on_user_performance_cookie_consent_withdrawn",
    },
    functional: {
        granted: "on_user_functional_cookie_consent_granted",
        denied: "on_user_functional_cookie_consent_withdrawn",
    },
    social_media: {
        granted: "on_user_social_media_cookie_consent_granted",
        denied: "on_user_social_media_cookie_consent_withdrawn",
    },
};

const PERFORMANCE_COOKIE_EXACT_NAMES = [
    "_ga",
    "_gid",
    "_gat",
    "_clck",
    "_clsk",
    "CLID",
    "MC_CID",
    "initialTrafficSource",
    "utmzses",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
];

const PERFORMANCE_COOKIE_PREFIXES = [
    "_ga_",
];

const AD_COOKIE_EXACT_NAMES = [
    "_gcl_au",
    "_gcl_aw",
    "_gcl_dc",
    "_gcl_gs",
    "gclid",
    "_fbp",
    "_fbc",
    "_uetsid",
    "_uetvid",
    "_uetmsclkid",
    "MUID",
    "MR",
    "ANONCHK",
    "SM",
];

const AD_COOKIE_PREFIXES = [
    "_gcl_",
];

const UTM_QUERY_PARAMS = [
    "utm_source",
    "utm_term",
    "utm_campaign",
    "utm_medium",
    "utm_content",
];

// Set this to true only if GTM is temporarily relying on these custom events firing on every page load.
const EMIT_CATEGORY_EVENTS_ON_PAGE_LOAD = false;

/**
 * Normalized internal representation of the user's optional consent choices.
 *
 * @typedef {Object} ConsentState
 * @property {boolean} performanceGranted - Analytics/performance consent. Used for GA4 analytics, Clarity analytics, attribution cookies, and UTM persistence.
 * @property {boolean} functionalGranted - Functional consent. Used for features such as maps, chat, personalization, and other non-essential UX enhancements.
 * @property {boolean} socialGranted - Social/advertising consent. Used for ads, remarketing, Bing UET, Meta, YouTube advertising behavior, and similar vendors.
 */

/**
 * Low-level cookie writer used by consent and cleanup helpers.
 *
 * @param {string} name
 * @param {string|boolean|number} value
 * @param {number} days
 * @param {{ path?: string, domain?: string, secure?: boolean, sameSite?: "Lax" | "Strict" | "None" }} [options]
 */
function setCookie(name, value, days, options = {}) {
    const {
        path = "/",
        domain,
        secure = window.location.protocol === "https:",
        sameSite = "Lax",
    } = options;

    const expires = Number.isFinite(days)
        ? `; expires=${new Date(Date.now() + days * 864e5).toUTCString()}`
        : "";

    const domainPart = domain ? `; domain=${domain}` : "";
    const securePart = secure ? "; Secure" : "";
    const encodedValue = encodeURIComponent(String(value));

    document.cookie = `${name}=${encodedValue}${expires}; path=${path}${domainPart}${securePart}; SameSite=${sameSite}`;
}

/**
 * Reads a first-party cookie by name.
 *
 * @param {string} name
 * @returns {string}
 */
function getCookie(name) {
    const cookie = document.cookie
        .split(";")
        .map((part) => part.trim())
        .find((part) => part.startsWith(`${name}=`));

    return cookie ? decodeURIComponent(cookie.substring(name.length + 1)) : "";
}

/**
 * Returns the safest domain option for MasterCraft consent cookies.
 * Uses `.mastercraft.com` only on MasterCraft-owned hostnames.
 *
 * @returns {{ domain?: string }}
 */
function getConsentCookieOptions() {
    const host = window.location.hostname.toLowerCase();

    if (host === "localhost" || host === "127.0.0.1") {
        return {};
    }

    if (host === "mastercraft.com" || host.endsWith(".mastercraft.com")) {
        return { domain: ".mastercraft.com" };
    }

    return {};
}

function createConsentCookie(name, value, days = COOKIE_EXPIRY_DAYS) {
    setCookie(name, value, days, getConsentCookieOptions());
}

function getUrlParameter(name) {
    const params = new URLSearchParams(window.location.search);
    return params.has(name) ? decodeURIComponent(params.get(name) || "") : "";
}

function isConsentGranted(cookieName) {
    const value = getCookie(cookieName);
    return value === "true" || value === "1";
}

/**
 * Reads the stored consent cookies and converts them to one normalized object.
 *
 * @returns {ConsentState}
 */
function getConsentState() {
    return {
        performanceGranted: isConsentGranted("user_performance_cookie_consent"),
        functionalGranted: isConsentGranted("user_functional_cookie_consent"),
        socialGranted: isConsentGranted("user_social_media_cookie_consent"),
    };
}

/**
 * Saves all category consent cookies.
 *
 * `user_cookie_consent` is stored as true once the user has made any choice,
 * including Reject All. This lets the banner remain hidden while optional
 * category cookies store the actual grant/deny values.
 *
 * @param {ConsentState} state
 */
function saveConsentState(state) {
    CONSENT_COOKIES.essential.forEach((key) => createConsentCookie(key, true));

    createConsentCookie("user_performance_cookie_consent", state.performanceGranted);
    createConsentCookie("user_functional_cookie_consent", state.functionalGranted);
    createConsentCookie("user_social_media_cookie_consent", state.socialGranted);
}

/**
 * Saves basic consent audit metadata as first-party cookies as browser-side
 * record to identify when, how, and against which policy version the user made their choice.
 *
 * @param {"accept_all" | "custom_preferences" | "reject_all" | "global_privacy_control"} source
 */
function saveConsentAuditMetadata(source) {
    createConsentCookie("user_cookie_choice_timestamp", new Date().toISOString());
    createConsentCookie("user_cookie_consent_version", CONSENT_VERSION);
    createConsentCookie("user_cookie_policy_version", COOKIE_POLICY_VERSION);
    createConsentCookie("user_cookie_choice_source", source);
}

/**
 * Deletes a first-party cookie from likely host/domain combinations.
 *
 * @param {string} name
 */
function deleteCookieEverywhere(name) {
    const host = window.location.hostname.toLowerCase();
    const expiration = "Thu, 01 Jan 1970 00:00:00 GMT";

    const domains = new Set([
        host,
        `.${host.replace(/^www\./, "")}`,
        ".mastercraft.com",
    ]);

    domains.forEach((domain) => {
        document.cookie = `${name}=; expires=${expiration}; Max-Age=0; path=/; domain=${domain}; Secure; SameSite=Lax`;
        document.cookie = `${name}=; expires=${expiration}; Max-Age=0; path=/; domain=${domain}; Secure; SameSite=None`;
    });

    document.cookie = `${name}=; expires=${expiration}; Max-Age=0; path=/; SameSite=Lax`;
}

function getCurrentCookieNames() {
    return document.cookie
        .split(";")
        .map((cookie) => cookie.trim().split("=")[0])
        .filter(Boolean);
}

function clearCookiesByNameAndPrefix(exactNames, prefixes) {
    const currentCookieNames = getCurrentCookieNames();
    const namesToDelete = new Set();

    exactNames.forEach((name) => {
        if (currentCookieNames.includes(name)) {
            namesToDelete.add(name);
        }
    });

    currentCookieNames.forEach((name) => {
        if (prefixes.some((prefix) => name.startsWith(prefix))) {
            namesToDelete.add(name);
        }
    });

    namesToDelete.forEach(deleteCookieEverywhere);
}

function clearPerformanceCookies() {
    clearCookiesByNameAndPrefix(PERFORMANCE_COOKIE_EXACT_NAMES, PERFORMANCE_COOKIE_PREFIXES);
}

function clearAdvertisingCookies() {
    clearCookiesByNameAndPrefix(AD_COOKIE_EXACT_NAMES, AD_COOKIE_PREFIXES);
}

function clearAllTrackingCookies() {
    clearPerformanceCookies();
    clearAdvertisingCookies();
}

/**
 * Updates Google Consent Mode from the internal consent state.
 *
 * Mapping:
 * - Performance -> analytics_storage
 * - Functional -> functionality_storage and personalization_storage
 * - Social/Advertising -> ad_storage, ad_user_data, ad_personalization
 * - Security -> always granted for strictly necessary security behavior
 *
 * @param {ConsentState} state
 */
function updateGoogleConsentMode(state) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtagFallback() {
        window.dataLayer.push(arguments);
    };

    window.gtag("consent", "update", {
        analytics_storage: state.performanceGranted ? "granted" : "denied",
        functionality_storage: state.functionalGranted ? "granted" : "denied",
        personalization_storage: state.functionalGranted ? "granted" : "denied",
        ad_storage: state.socialGranted ? "granted" : "denied",
        ad_user_data: state.socialGranted ? "granted" : "denied",
        ad_personalization: state.socialGranted ? "granted" : "denied",
        security_storage: "granted",
    });
}

/**
 * Updates Microsoft UET consent.
 *
 * @param {ConsentState} state
 */
function updateMicrosoftUETConsent(state) {
    window.uetq = window.uetq || [];

    window.uetq.push("consent", "update", {
        ad_storage: state.socialGranted ? "granted" : "denied",
    });
}

/**
 * Updates Meta Pixel consent if fbq exists or the layout-level bridge is installed.
 *
 * @param {ConsentState} state
 */
function updateMetaConsent(state) {
    if (typeof window.__mcApplyMetaConsent === "function") {
        window.__mcApplyMetaConsent(state);
        return;
    }

    if (typeof window.fbq === "function") {
        window.fbq("consent", state.socialGranted ? "grant" : "revoke");
    }
}

/**
 * Updates Microsoft Clarity consent if Clarity exists on the page.
 * @param {ConsentState} state
 */
function updateClarityConsent(state) {
    if (typeof window.__mcApplyClarityConsent === "function") {
        window.__mcApplyClarityConsent(state);
        return;
    }

    // Fallback only. This protects local/dev pages where the layout bridge is missing.
    if (!window.clarity) return;

    window.clarity("consentv2", {
        analytics_Storage: state.performanceGranted ? "granted" : "denied",
        ad_Storage: state.performanceGranted && state.socialGranted ? "granted" : "denied",
    });

    window.clarity("consent", state.performanceGranted);
}

function pushConsentEvent(category, granted) {
    const consentState = granted ? "granted" : "denied";
    const eventName = CONSENT_EVENTS[category][consentState];

    document.dispatchEvent(new Event(eventName));

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: eventName,
        consent_category: category,
        consent_state: consentState,
        consent_version: CONSENT_VERSION,
        cookie_policy_version: COOKIE_POLICY_VERSION,
        consent_timestamp: new Date().toISOString(),
    });
}

function pushConsentSnapshot(state) {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
        event: "mc_consent_state",
        performanceConsent: state.performanceGranted,
        functionalConsent: state.functionalGranted,
        socialMediaConsent: state.socialGranted,
        consent_version: CONSENT_VERSION,
        cookie_policy_version: COOKIE_POLICY_VERSION,
        consent_timestamp: new Date().toISOString(),
    });
}

/**
 * Deletes known first-party tracking cookies for denied categories.
 * - If performance is denied, analytics/attribution cookies are removed.
 * - If social/advertising is denied, ad/conversion cookies are removed.
 * @param {ConsentState} state
 */
function clearCookiesForDeniedConsent(state) {
    if (!state.performanceGranted) {
        clearPerformanceCookies();
    }

    if (!state.socialGranted) {
        clearAdvertisingCookies();
    }
}

/**
 * Canonical consent update pipeline.
 *
 * Stages:
 * 1. Read the current consent cookies into one normalized state object.
 * 2. Update vendor consent APIs first so Google, UET, and Clarity receive the latest state.
 * 3. Dispatch category-specific DOM/dataLayer events for GTM triggers and existing listeners.
 * 4. Push one full-state `mc_consent_state` event for debugging and GTM variables.
 * 5. Remove known first-party tracking cookies for any denied categories.
 *
 * Use this function after every consent change and on every page load.
 *
 * @param {{ clearDeniedCookies?: boolean,emitCategoryEvents?: boolean,pushSnapshot?: boolean }} [options]
 * @returns {ConsentState}
 */
function applyConsentState(options = {}) {
    const { clearDeniedCookies = true,
            emitCategoryEvents = false,
            pushSnapshot = true
          } = options;
    const state = getConsentState();

    window.__mcInitialConsentState = state;

    // Stage 1: Vendor consent APIs.
    updateGoogleConsentMode(state);
    updateMicrosoftUETConsent(state);
    updateMetaConsent(state);
    updateClarityConsent(state);

    // Stage 2: Category-specific events used by GTM and inline listeners.
    if (emitCategoryEvents) {
        pushConsentEvent("performance", state.performanceGranted);
        pushConsentEvent("functional", state.functionalGranted);
        pushConsentEvent("social_media", state.socialGranted);
    }


    // Stage 3: Full consent snapshot for debugging and GTM variables.
    if (pushSnapshot) {
        pushConsentSnapshot(state);
    }
    // Stage 4: Cleanup cookies that no longer have consent.
    if (clearDeniedCookies) {
        clearCookiesForDeniedConsent(state);
    }

    return state;
}

function syncConsentSwitches(state) {
    $("#performanceCookieSwitch").prop("checked", state.performanceGranted);
    $("#functionalCookieSwitch").prop("checked", state.functionalGranted);
    $("#socialmediaCookieSwitch").prop("checked", state.socialGranted);
}

function addAllConsentCookies() {
    const state = {
        performanceGranted: true,
        functionalGranted: true,
        socialGranted: true,
    };

    saveConsentState(state);
    saveConsentAuditMetadata("accept_all");
    syncConsentSwitches(state);
    applyConsentState({ emitCategoryEvents: true, clearDeniedCookies: false });
    loadUTMParameters();
    hideCookieConsent();
}

function addCustomConsentCookies() {
    const state = {
        performanceGranted: $("#performanceCookieSwitch").is(":checked"),
        functionalGranted: $("#functionalCookieSwitch").is(":checked"),
        socialGranted: $("#socialmediaCookieSwitch").is(":checked"),
    };

    saveConsentState(state);
    saveConsentAuditMetadata("custom_preferences");
    syncConsentSwitches(state);
    applyConsentState({emitCategoryEvents: true,clearDeniedCookies: true});

    if (state.performanceGranted) {
        loadUTMParameters();
    }
    hideCookieConsent();
}

function rejectAllConsentCookies() {
    const state = {
        performanceGranted: false,
        functionalGranted: false,
        socialGranted: false,
    };

    saveConsentState(state);
    saveConsentAuditMetadata("reject_all");
    syncConsentSwitches(state);

    applyConsentState({ emitCategoryEvents: true,clearDeniedCookies: true});
    hideCookieConsent();
}

function isGlobalPrivacyControlEnabled() {
    return navigator.globalPrivacyControl === true || window.globalPrivacyControl === true;
}

/**
 * Applies Global Privacy Control as a browser-level opt-out.
 */
function applyGlobalPrivacyControl() {
    if (!isGlobalPrivacyControlEnabled()) return false;

    const previousState = getConsentState();
    const previousSource = getCookie("user_cookie_choice_source");

    const state = {
        performanceGranted: false,
        functionalGranted: false,
        socialGranted: false,
    };

    const hadOptionalConsent = previousState.performanceGranted || previousState.functionalGranted || previousState.socialGranted;

    saveConsentState(state);

    // Avoid rewriting the audit timestamp on every page load when GPC is already applied.
    if (hadOptionalConsent || previousSource !== "global_privacy_control") {
        saveConsentAuditMetadata("global_privacy_control");
    }

    syncConsentSwitches(state);
    applyConsentState({ emitCategoryEvents: hadOptionalConsent, clearDeniedCookies: true });
    hideCookieConsent();
    showGPCConfirmation();
    return true;
}

function loadUTMParameters() {
    if (!isConsentGranted("user_performance_cookie_consent")) return;
    if (!window.location.search.includes("utm_")) return;

    UTM_QUERY_PARAMS.forEach((param) => {
        const value = getUrlParameter(param);
        if (value) {
            createConsentCookie(param, value, 1);
        }
    });
}

function showCookieConsent() {
    $("#cookieBox").removeClass("opacity-0 hidden").addClass("show");
}

function hideCookieConsent() {
    $("#cookieBox").removeClass("show").addClass("hidden");
}

function showGPCConfirmation() {
    document.getElementById("gpc-confirmation-cookie-box")?.classList.remove("hidden");
    document.getElementById("gpc-confirmation-cookie-modal")?.classList.remove("hidden");
}

function checkBoxToggle(box) {
    const $box = $(box);
    $box.prop("checked", !$box.prop("checked"));
}

function checkConsentCookies() {
    if (isGlobalPrivacyControlEnabled()) {
        hideCookieConsent();
        showGPCConfirmation();
        return;
    }

    if (isConsentGranted("user_cookie_consent")) {
        hideCookieConsent();
    } else {
        showCookieConsent();
    }
}

function scrollToCcpaSection() {
    if (!window.location.href.includes("CCPA")) return;

    setTimeout(() => {
        document.getElementById("CCPA")?.scrollIntoView();
    }, 2000);
}

//function bindCookieConsentEvents() {
//    $(document).on("click", "#show-cookie-consent", showCookieConsent);

//    $(document).on(
//        "click",
//        "#cookie-modal-all-accept, #cookie-banner-close-btn-US, #cookie-accept-btn-intl",
//        addAllConsentCookies
//    );

//    $(document).on("click", "#cookie-modal-custom-accept", addCustomConsentCookies);

//    $(document).on(
//        "click",
//        "#cookie-modal-reject-all, #cookie-reject-all-btn, #cookie-reject-all-btn-intl",
//        rejectAllConsentCookies
//    );

//    $(document).on("keypress", ".cookie-settings-switch", function onCookieSwitchKeypress(event) {
//        if (event.key === "Enter" || event.keyCode === 13) {
//            checkBoxToggle(this);
//        }

//        event.stopPropagation();
//    });
//}

function bindCookieConsentEvents() {
    // Prevent duplicate bindings if this initializer ever runs more than once.
    $(document).off(".mcCookieConsent");

    $(document).on("click.mcCookieConsent", "#show-cookie-consent", showCookieConsent);

    $(document).on(
        "click.mcCookieConsent",
        "#cookie-modal-all-accept, #cookie-banner-close-btn-US, #cookie-accept-btn-intl",
        addAllConsentCookies
    );

    $(document).on(
        "click.mcCookieConsent",
        "#cookie-modal-custom-accept",
        addCustomConsentCookies
    );

    $(document).on(
        "click.mcCookieConsent",
        "#cookie-modal-reject-all, #cookie-reject-all-btn, #cookie-reject-all-btn-intl",
        rejectAllConsentCookies
    );

    $(document).on("keydown.mcCookieConsent", ".cookie-settings-switch", function onCookieSwitchKeydown(event) {
        const isToggleKey = event.key === "Enter" || event.key === " ";

        if (!isToggleKey) return;

        event.preventDefault();
        event.stopPropagation();

        checkBoxToggle(this);
    });
}

/**
 * Initializes cookie consent behavior once the DOM is ready.
 *
 * Order:
 * 1. GPC is applied first because it overrides optional consent.
 * 2. Stored consent is replayed into vendor APIs and GTM on every page load.
 * 3. Banner visibility is decided after the stored state is applied.
 * 4. UTM cookies are loaded only if performance consent is currently granted.
 * 5. UI event handlers are bound last.
 */
function initCookieConsent() {
    scrollToCcpaSection();

    const gpcApplied = applyGlobalPrivacyControl();
    if (!gpcApplied) {
        applyConsentState({emitCategoryEvents: EMIT_CATEGORY_EVENTS_ON_PAGE_LOAD,clearDeniedCookies: true});
    }

    checkConsentCookies();
    loadUTMParameters();
    bindCookieConsentEvents();
}

document.addEventListener("DOMContentLoaded", initCookieConsent);

window.getCookie = getCookie;
window.createConsentCookie = createConsentCookie;
window.loadUTMParameters = loadUTMParameters;
window.rejectAllConsentCookies = rejectAllConsentCookies;
window.applyConsentState = applyConsentState;
window.clearAllTrackingCookies = clearAllTrackingCookies;
